import { TableViewConfig } from "../../../hooks/useTableViewConfig";
import { DEFAULT_VISIBLE_IDS } from "./columns";
import { ViewPreset } from "./ViewsMenu";

/**
 * What changes between the WIP tables that share BuyMediaWipTable: where each
 * one keeps its view, which columns it starts with and its built-in views.
 * The column registry (columns.tsx) is the same for both.
 */
export interface WipTableVariant {
  /** localStorage key of the working view (columns, order, widths, filters, sort). */
  storageKey: string;
  /** Backend key of the table's saved views. */
  tableKey: string;
  /** localStorage key of the view loaded in this browser. */
  activeViewKey: string;
  /**
   * localStorage key of the column ids the saved view already knew about. A
   * saved view lists only the columns visible when it was saved, so without it
   * a column added later would stay hidden for everyone who had customised it.
   */
  knownColumnsKey: string;
  /** Columns added before knownColumnsKey existed: the first run treats them as new. */
  columnsAddedBeforeTracking: string[];
  defaultConfig: TableViewConfig;
  presets: ViewPreset[];
  /** Exported file name, without extension. */
  csvName: string;
  /** Legend under the table (status colours, ideal margin, "Aceptar"). */
  showLegend: boolean;
}

const configWith = (visibleColumnIds: string[]): TableViewConfig => ({
  visibleColumnIds,
  statusFilter: [],
  textFilter: "",
  fieldFilters: {},
  columnWidths: {},
  columnOrder: [],
  sort: null,
});

// realPlatform is admin-only: the table drops it for everybody else.
const IDENTIFICATION_IDS = [
  "companyName",
  "associatedCampaignName",
  "platform",
  "briefingCampaignType",
  "realPlatform",
];

// ── Buy Media Direct & Own ────────────────────────────────────────────────────

export const BUY_MEDIA_TABLE: WipTableVariant = {
  storageKey: "beapolo_wip_buymedia_view",
  tableKey: "wip_buymedia",
  activeViewKey: "beapolo_wip_buymedia_active_view",
  knownColumnsKey: "beapolo_wip_buymedia_known_columns",
  columnsAddedBeforeTracking: ["marginIdeal"],
  defaultConfig: configWith(DEFAULT_VISIBLE_IDS),
  csvName: "BuyMedia",
  showLegend: true,
  presets: [
    { id: "standard", name: "Estándar", visibleColumnIds: DEFAULT_VISIBLE_IDS },
    {
      id: "margin",
      name: "Margen",
      visibleColumnIds: [
        ...IDENTIFICATION_IDS,
        "briefingName",
        "startDate",
        "endDate",
        "objective",
        "grossUnitCost",
        "netUnitCost",
        "realUnitCostPlatform",
        "totalInversion",
        "margin",
        "marginIdeal",
        "platformInversion",
        "status",
        "totalPlatformCost",
        "pacingInvestmentPercent",
        "investmentNeededTotalReal",
      ],
    },
    {
      id: "pacing",
      name: "Pacing",
      visibleColumnIds: [
        ...IDENTIFICATION_IDS,
        "startDate",
        "endDate",
        "objective",
        "targetVolume",
        "status",
        "expectedVolumeUntilYesterday",
        "accumulatedVolumeUntilYesterday",
        "volumeVariation",
        "totalPlatformCost",
        "pacingInvestmentPercent",
        "remainingDays",
        "dailyTargetVolume",
        "yesterdayActualVolume",
        "yesterdayVolumeVar",
      ],
    },
  ],
};

// ── Buy Media Direct & Own · Off ──────────────────────────────────────────────

// Compras directas: la línea y solo los datos que se rellenan a mano.
const DIRECT_BUY_OFF_VISIBLE_IDS = [
  "companyName",
  "briefingName",
  "associatedCampaignName",
  "platform",
  "briefingCampaignType",
  "startDate",
  "endDate",
  "impressions",
  "clicks",
  "completeViews",
  "conversions",
  "uniqueUsers",
  "totalPlatformCost",
];

export const DIRECT_BUY_OFF_TABLE: WipTableVariant = {
  storageKey: "beapolo_wip_directbuyoff_view",
  tableKey: "wip_directbuyoff",
  activeViewKey: "beapolo_wip_directbuyoff_active_view",
  knownColumnsKey: "beapolo_wip_directbuyoff_known_columns",
  columnsAddedBeforeTracking: [],
  defaultConfig: configWith(DIRECT_BUY_OFF_VISIBLE_IDS),
  csvName: "BuyMediaOff",
  showLegend: false,
  presets: [
    {
      id: "standard",
      name: "Estándar",
      visibleColumnIds: DIRECT_BUY_OFF_VISIBLE_IDS,
    },
  ],
};

// ── Paid Media (gestionado) ───────────────────────────────────────────────────

const PAID_DEFAULT_VISIBLE_IDS = [
  ...IDENTIFICATION_IDS,
  "briefingName",
  "campaignFormat",
  "investmentPayment",
  "startDate",
  "endDate",
  "objective",
  "targetVolume",
  "grossUnitCost",
  "totalInversion",
  "margin",
  "platformInversion",
  "impressions",
  "clicks",
  "completeViews",
  "conversions",
  "engagements",
  "uniqueUsers",
  "frequency",
  "status",
  "expectedVolumeUntilYesterday",
  "accumulatedVolumeUntilYesterday",
  "obtainedVolumePercent",
  "volumeVariation",
  "totalPlatformCost",
  "accumulatedClientCost",
  "pacingInvestmentPercent",
  "realUnitCostClient",
  "clickThroughRate",
  "viewThroughRate",
];

export const PAID_MEDIA_TABLE: WipTableVariant = {
  storageKey: "beapolo_wip_paidmedia_view",
  tableKey: "wip_paidmedia",
  activeViewKey: "beapolo_wip_paidmedia_active_view",
  knownColumnsKey: "beapolo_wip_paidmedia_known_columns",
  columnsAddedBeforeTracking: [],
  defaultConfig: configWith(PAID_DEFAULT_VISIBLE_IDS),
  csvName: "PaidMedia",
  showLegend: false,
  // One view per kind of buy, with the delivery columns that kind is judged by.
  presets: [
    {
      id: "standard",
      name: "Estándar",
      visibleColumnIds: PAID_DEFAULT_VISIBLE_IDS,
    },
    {
      id: "social",
      name: "Social",
      visibleColumnIds: [
        ...IDENTIFICATION_IDS,
        "objective",
        "status",
        "impressions",
        "uniqueUsers",
        "frequency",
        "clicks",
        "clickThroughRate",
        "engagements",
        "engagementRate",
        "conversions",
        "accumulatedClientCost",
        "cpmReal",
        "cpcReal",
        "cpeReal",
        "cpaReal",
        "pacingInvestmentPercent",
      ],
    },
    {
      id: "video",
      name: "Vídeo",
      visibleColumnIds: [
        ...IDENTIFICATION_IDS,
        "campaignFormat",
        "objective",
        "status",
        "impressions",
        "firstQuartileViews",
        "midpointViews",
        "thirdQuartileViews",
        "completeViews",
        "viewThroughRate",
        "accumulatedClientCost",
        "cpvReal",
        "cpmReal",
        "pacingInvestmentPercent",
      ],
    },
    {
      id: "display",
      name: "Display",
      visibleColumnIds: [
        ...IDENTIFICATION_IDS,
        "campaignFormat",
        "objective",
        "status",
        "impressions",
        "measurableImpressions",
        "viewableImpressions",
        "viewabilityRate",
        "clicks",
        "clickThroughRate",
        "accumulatedClientCost",
        "cpmReal",
        "cpcReal",
        "pacingInvestmentPercent",
      ],
    },
    {
      id: "performance",
      name: "Performance",
      visibleColumnIds: [
        ...IDENTIFICATION_IDS,
        "objective",
        "status",
        "targetVolume",
        "accumulatedVolumeUntilYesterday",
        "clicks",
        "conversions",
        "conversionRate",
        "accumulatedClientCost",
        "cpcReal",
        "cpaReal",
        "pacingInvestmentPercent",
      ],
    },
    {
      id: "pacing",
      name: "Pacing",
      visibleColumnIds: [
        ...IDENTIFICATION_IDS,
        "startDate",
        "endDate",
        "objective",
        "targetVolume",
        "status",
        "expectedVolumeUntilYesterday",
        "accumulatedVolumeUntilYesterday",
        "volumeVariation",
        "totalPlatformCost",
        "pacingInvestmentPercent",
        "remainingDays",
        "dailyTargetVolume",
        "yesterdayActualVolume",
        "yesterdayVolumeVar",
      ],
    },
  ],
};
